'use es6';

import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import Controllable from 'UIComponents/decorators/Controllable';
import lazyEval from 'UIComponents/utils/lazyEval';
import ColorPicker from '../ColorPicker';
import { ColorPickerModes, DefaultColorGrid } from '../constants';
import SquatchGrid from './SquatchGrid';
import { isValidFavorites } from '../utils/validateColor';
import { Button } from 'foundations-components/transitional/components/Button';
import { PopoverBody } from 'foundations-components/transitional/components/Popover';
import { Link } from 'foundations-components/transitional/components/Link';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FavoritesLabelContainer = styled.div.withConfig({
  displayName: "ColorPickerPopoverBody__FavoritesLabelContainer"
})(({
  theme
}) => ({
  marginBlockEnd: theme.space['50'],
  display: 'flex',
  justifyContent: 'space-between'
}));
const FavoritesDiv = styled.div.withConfig({
  displayName: "ColorPickerPopoverBody__FavoritesDiv"
})(({
  theme
}) => ({
  marginBlockStart: theme.space['200']
}));
const RoundedTabs = styled.div.withConfig({
  displayName: "ColorPickerPopoverBody__RoundedTabs"
})({
  borderStartStartRadius: 'inherit',
  borderStartEndRadius: 'inherit',
  overflow: 'hidden'
});
class ColorPickerPopoverBody extends Component {
  constructor(...args) {
    super(...args);
    this.handleResetClick = () => {
      const {
        defaultAlpha,
        defaultColor,
        onChange,
        onResetClick,
        onOpenChange
      } = this.props;
      if (onResetClick) {
        onResetClick();
      } else {
        onChange({
          color: defaultColor,
          alpha: defaultAlpha
        });
      }

      // Always close the picker on reset
      if (onOpenChange) {
        onOpenChange(SyntheticEvent(false));
      }
    };
    this.handleColorChange = ({
      color,
      alpha
    }) => {
      const {
        onChange,
        onOpenChange,
        pickerMode
      } = this.props;
      onChange({
        color,
        alpha
      });

      // Close the picker when on the SIMPLE tab
      if (pickerMode === ColorPickerModes.SIMPLE && onOpenChange) {
        onOpenChange(SyntheticEvent(false));
      }
    };
  }
  renderFavoritesLink() {
    const {
      editFavoritesLink,
      showFavoritesLink
    } = this.props;
    if (!editFavoritesLink || !showFavoritesLink) {
      return null;
    }
    return /*#__PURE__*/_jsx(Link, {
      external: true,
      href: editFavoritesLink,
      children: I18n.text('colorPicker.popover.edit')
    });
  }
  renderFavorites() {
    const {
      color,
      favorites,
      editFavoritesLabel
    } = this.props;
    if (!isValidFavorites(favorites)) {
      return null;
    }
    return /*#__PURE__*/_jsxs(FavoritesDiv, {
      "data-test-id": "colorpicker-favorites",
      children: [/*#__PURE__*/_jsxs(FavoritesLabelContainer, {
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "0",
          children: lazyEval(editFavoritesLabel)
        }), this.renderFavoritesLink()]
      }), /*#__PURE__*/_jsx(SquatchGrid, {
        currentColor: color,
        grid: favorites,
        onClick: this.handleColorChange
      })]
    });
  }
  renderReset() {
    const {
      showResetToDefault
    } = this.props;
    if (!showResetToDefault) {
      return null;
    }
    return /*#__PURE__*/_jsx(TFlex, {
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "colorpicker-reset-button",
        onClick: this.handleResetClick,
        size: "small",
        use: "tertiary-light",
        inlineSize: "100%",
        marginBlockStart: "300",
        children: I18n.text('colorPicker.popover.reset')
      })
    });
  }
  renderSimpleTab() {
    const {
      color,
      colorGrid,
      favorites
    } = this.props;
    const isFavoriteColor = color && isValidFavorites(favorites) && favorites.indexOf(color.toLowerCase()) >= 0;
    return /*#__PURE__*/_jsxs(Tab, {
      onMouseDown: evt => evt.preventDefault(),
      tabId: ColorPickerModes.SIMPLE,
      title: I18n.text('colorPicker.popover.mode.simple'),
      children: [/*#__PURE__*/_jsx(SquatchGrid, {
        currentColor: color,
        grid: colorGrid,
        onClick: this.handleColorChange,
        showMatchedSquatch: !isFavoriteColor
      }), this.renderFavorites(), this.renderReset()]
    });
  }
  renderAdvancedTab() {
    const {
      alpha,
      color,
      includeAlpha
    } = this.props;
    return /*#__PURE__*/_jsxs(Tab, {
      onMouseDown: evt => evt.preventDefault(),
      tabId: ColorPickerModes.ADVANCED,
      title: I18n.text('colorPicker.popover.mode.advanced'),
      children: [/*#__PURE__*/_jsx(ColorPicker, {
        alpha: alpha,
        color: color,
        fieldsetSize: "small",
        hexOnly: false,
        includeAlpha: includeAlpha,
        onChange: this.handleColorChange,
        padding: '0'
      }), this.renderReset()]
    });
  }
  render() {
    const {
      onPickerModeChange,
      pickerMode
    } = this.props;
    return /*#__PURE__*/_jsx(PopoverBody, {
      flush: true,
      "data-test-id": "colorpicker-popover-body",
      children: /*#__PURE__*/_jsx(RoundedTabs, {
        children: /*#__PURE__*/_jsxs(Tabs, {
          fill: true,
          onSelectedChange: onPickerModeChange,
          selected: pickerMode,
          use: "toolbar",
          panelStyles: {
            padding: '400',
            margin: '0'
          },
          children: [this.renderSimpleTab(), this.renderAdvancedTab()]
        })
      })
    });
  }
}
/**
 * @typedef {string | {color: string, alpha: string}} ColorGridItem
 *
 * @typedef {Object} ColorPickerPopoverBodyProps
 * @property {number} [alpha]
 * @property {string} [color]
 * @property {ColorGridItem[]} [colorGrid]
 * @property {number} [defaultAlpha]
 * @property {string} [defaultColor]
 * @property {string | function(): string} [editFavoritesLabel]
 * @property {string} [editFavoritesLink]
 * @property {string[]} [favorites]
 * @property {boolean} [includeAlpha]
 * @property {function({color?: string, alpha?: number}): void} onChange - required
 * @property {function(): void} [onOpenChange]
 * @property {function('advanced'|'brand'|'simple'): void} [onPickerModeChange]
 * @property {function(): void} [onResetClick]
 * @property {'advanced'|'brand'|'simple'} [pickerMode]
 * @property {boolean} [showFavoritesLink]
 * @property {boolean} [showResetToDefault]
 */
ColorPickerPopoverBody.defaultProps = {
  colorGrid: DefaultColorGrid,
  defaultAlpha: 100,
  defaultColor: '#FFFFFF',
  includeAlpha: false,
  pickerMode: ColorPickerModes.SIMPLE,
  onPickerModeChange: emptyFunction.thatReturnsNull,
  showResetToDefault: true,
  editFavoritesLabel: () => I18n.text('colorPicker.favorites')
};
export default Controllable(ColorPickerPopoverBody, ['pickerMode']);